import { useEffect } from 'react';

const upsertMeta = (selector, attributes) => {
  let tag = document.head.querySelector(selector);
  let created = false;

  if (!tag) {
    tag = document.createElement('meta');
    Object.entries(attributes.identity).forEach(([key, value]) =>
      tag.setAttribute(key, value),
    );
    document.head.appendChild(tag);
    created = true;
  }

  const previous = tag.getAttribute('content');
  tag.setAttribute('content', attributes.content);

  return () => {
    if (created) {
      tag.remove();
    } else if (previous !== null) {
      tag.setAttribute('content', previous);
    }
  };
};

/**
 * SPA에서 페이지별 <title>과 SEO/OG 메타태그를 설정한다.
 * 언마운트 시 이전 값으로 되돌린다.
 *
 * 주의: CRA는 클라이언트 렌더라 크롤러가 JS를 실행하지 않으면 이 태그를 못 본다.
 * 검색·SNS 미리보기가 중요해지면 프리렌더링(SSR/SSG)을 별도로 검토해야 한다.
 */
const useDocumentMeta = ({ title, description, image, url } = {}) => {
  useEffect(() => {
    if (!title && !description && !image) return undefined;

    const cleanups = [];
    const previousTitle = document.title;

    if (title) document.title = title;

    if (description) {
      cleanups.push(
        upsertMeta('meta[name="description"]', {
          identity: { name: 'description' },
          content: description,
        }),
        upsertMeta('meta[property="og:description"]', {
          identity: { property: 'og:description' },
          content: description,
        }),
      );
    }

    if (title) {
      cleanups.push(
        upsertMeta('meta[property="og:title"]', {
          identity: { property: 'og:title' },
          content: title,
        }),
      );
    }

    if (image) {
      cleanups.push(
        upsertMeta('meta[property="og:image"]', {
          identity: { property: 'og:image' },
          content: image,
        }),
      );
    }

    if (url) {
      cleanups.push(
        upsertMeta('meta[property="og:url"]', {
          identity: { property: 'og:url' },
          content: url,
        }),
      );
    }

    cleanups.push(
      upsertMeta('meta[property="og:type"]', {
        identity: { property: 'og:type' },
        content: 'article',
      }),
    );

    return () => {
      document.title = previousTitle;
      cleanups.forEach((cleanup) => cleanup());
    };
  }, [title, description, image, url]);
};

export default useDocumentMeta;
